import { BoosterType } from '../../Card/BoosterType';
import { IGiftData } from '../GameActions';
import { IMessagePayload } from '../IEntryPoint';
import { IGameState } from '../IGameState';
import { InventoryStateHelper } from '../../Player/Inventory/InventoryStateHelper';
import { PlayerStateHelper } from '../../Player/PlayerStateHelper';
import { PlayerTask } from '../../Player/PlayerTasks';
import { IPlayerLevelData, LevelType } from '../../Player/Progress/PlayerLevels';
import { SocialStateHelper } from '../../Player/Social/SocialStateHelper';
import { PopupID } from '../../UI/PopupID';
import { track, TrackMethod } from 'Utility/Tracking/Track';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import Game from 'Game';
import { TActionType, TransactionType, TSocialActionType } from 'Utility/Tracking/DataSet/Transaction';
import { IBotMessagePayload } from 'Utility/EntryPoint/Payloads/IBotMessagePayload';
import { PayloadType } from 'Utility/SDK/PayloadType';
import { trackReward } from 'Utility/Tracking/TrackingHelpers/Resource';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { currencyShareReward, inviteShareReward } from 'Constants/Economy';
import { EntryOrigin } from 'Utility/Tracking/DataSet/EntryPoint';
import { report } from 'report';
import { FBPixel } from 'Utility/SDK/FBPixelHandler';

export default class PayloadStateHelper {
    public static handleEntryPointPayload(state: IGameState, payload: IMessagePayload): IGameState {
        console.log(state, payload);
        try {
            if (payload.entrypoint === 'A2U') {
                state = this.handleA2UPayload(state, payload);
            }
            // Handle xpromo
            if (payload.originGameId !== undefined || payload._value === 'game_switch') {
                state = this.handleXPromoPayload(state, payload);
            }

            // Handle bot payload
            if (payload && payload.entryPoint) {
                state = this.handleBotMessagePayload(state, payload);
            }

            // Handle internal payloads
            if (payload && payload.data) {
                state = this.handleInternalPayload(state, payload);
            }
            // Handle marketing campaign payloads
            if (payload && payload.entryPoint === 'adCampaign') {
                state = this.handleMarketingCampaignEntrypoint(state, payload);
            }
        } catch (error) {
            report("Failed to handle entry point", {
                once: true,
                sampleRate: 0.5,
                tags: {
                    placement: payload.placement,
                    data: payload.data
                },
                error,
                state
            });

        }

        state = {
            ...state,
            entryPoint: {
                ...state.entryPoint,
                handled: true
            }
        };

        return state;
    }

    private static handleXPromoPayload(state: IGameState, payload: IMessagePayload): IGameState {
        try {
            track('epPromoSource', payload.originGameId);

            Game.sdk.gameTracking(FBTrackingEvent.X_PROMO);
            Game.sdk.gameTracking(
                state.playerState.levels[LevelType.Core].length === 0
                    ? FBTrackingEvent.X_PROMO_NEW
                    : FBTrackingEvent.X_PROMO_OLD,
                undefined,
                {
                    reward: payload.promoReward ? 'yes' : 'no',
                    source: payload.originGameId,
                }
            );
        } catch (e) {
            console.warn(e);
        }

        return state;
    }

    private static handleA2UPayload(state: IGameState, payload: IMessagePayload): IGameState {
        // Validate this 'message' has not already been claimed.
        if (SocialStateHelper.hasReceivedMessage(state.playerState, payload.messageId)) {
            console.warn('message seen already.');
            return state;
        }
        let playerState = SocialStateHelper.retireMessage(
            state.playerState,
            payload.messageId
        )
        playerState = InventoryStateHelper.giveReward(playerState, payload.reward);

        return PlayerStateHelper.setPlayerState(state, playerState);
    }

    private static handleBotMessagePayload(
        state: IGameState,
        payload: { gamePayload?: IBotMessagePayload; [key: string]: any }
    ): IGameState {
        if (
            payload.gamePayload &&
            payload.gamePayload.gift &&
            !SocialStateHelper.hasReceivedMessage(state.playerState, payload.messageId)
        ) {
            track('tActionType', TActionType.BOT);

            state = PlayerStateHelper.setPlayerState(
                state,
                SocialStateHelper.retireMessage(state.playerState, payload.messageId)
            );

            payload.gamePayload.gift.giftData.forEach((giftData: IGiftData) => {
                state = {
                    ...state,
                    playerState: InventoryStateHelper.giveReward(
                        state.playerState,
                        {
                            type: giftData.itemId,
                            value: giftData.amount,
                        },
                        { queueTask: true }
                    ),
                };
            });
        }

        return state;
    }

    private static handleInternalPayload(state: IGameState, payload: IMessagePayload): IGameState {
        // Validate this 'message' has not already been claimed.
        if (SocialStateHelper.hasReceivedMessage(state.playerState, payload.messageId)) {
            console.warn('message seen already.');
            return state;
        }

        // Validate there is a userId with the payload and it is not the current user.
        if (!payload.userId || payload.userId === state.userState.userId) {
            return state;
        }

        // Add message ID to 'receivedMessageIDs' array.
        state = PlayerStateHelper.setPlayerState(
            state,
            SocialStateHelper.retireMessage(state.playerState, payload.messageId)
        );

        switch (payload.data) {
            case PayloadType.SEND_CURRENCY:
                Game.sdk.gameTracking(FBTrackingEvent.Coin_Send_Entry);
                state = {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.SHOW_POPUP,
                            popup: PopupID.GIFT_MENU,
                            meta: {
                                reward: payload.reward,
                                payload,
                            },
                        },
                    ]),
                };

                if (payload.reward) {
                    trackReward(payload.reward);
                }

                track('socialActionType', TSocialActionType.GET_COINS);
                track('socialRecieve', 1);
                trackResourceEvent(state, {
                    transactionType: TransactionType.REWARD,
                    tActionType: TActionType.SOCIAL,
                });

                return state;
            case PayloadType.REQUEST_CURRENCY:
                Game.sdk.gameTracking(FBTrackingEvent.Coin_Request_Entry);
                return {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(
                        state.playerState,
                        [
                            {
                                task: PlayerTask.SEND_COINS,
                                reward: currencyShareReward,
                                payload,
                            },
                        ],
                        true
                    ),
                };
            case PayloadType.HELP_FRIEND:
                Game.sdk.gameTracking(FBTrackingEvent.Helper_Entry);
                track('tHelperCards', 1, TrackMethod.ADD);
                state = {
                    ...state,
                    playerState: InventoryStateHelper.addInventory(state.playerState, BoosterType.PLUS_ONE, 1),
                };
                return {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.EXTRA_CARDS,
                            cards: payload.cards,
                            payload,
                        },
                    ]),
                };
            case PayloadType.INVITE_FRIEND:
                if (payload.placement === 'SocialHub') {
                    Game.sdk.gameTracking(FBTrackingEvent.SocialHub_Invite_Entry);
                } else if (payload.placement === 'TopList') {
                    Game.sdk.gameTracking(FBTrackingEvent.TopList_Invite_Entry);
                }

                const levels: IPlayerLevelData[] = state.playerState.levels[LevelType.Core];
                if (levels[0] != null && levels[0].stars != null) return state;

                state = {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.SEND_COINS,
                            reward: inviteShareReward,
                            payload,
                        },
                    ]),
                };

                track('socialActionType', TSocialActionType.INVITE);
                trackReward(inviteShareReward);
                track('socialRecieve', 1);
                trackResourceEvent(state, {
                    transactionType: TransactionType.REWARD,
                    tActionType: TActionType.INVITE,
                });
                return state;
            case PayloadType.SEND_FRIEND_GIFT:
                if (payload.placement === 'SagaMap') {
                    Game.sdk.gameTracking(FBTrackingEvent.SagaMap_Coins_Entry);
                } else if (payload.placement === 'TopList') {
                    Game.sdk.gameTracking(FBTrackingEvent.Top_List_Coins_Entry);
                }
                return state;
            default:
                return state;
        }
    }

    private static handleMarketingCampaignEntrypoint(
        state: IGameState,
        entrypointData: IMessagePayload & {
            fb_instant_game_ad_id?: number,
            fb_instant_game_adset_id?: number,
            fb_instant_game_campaign_id?: number,
        }
    ): IGameState {
        const payload: IMessagePayload = entrypointData.payload;
        const hasFBProperties: boolean = Boolean(entrypointData && entrypointData.fb_instant_game_ad_id);

        // Game.sdk.gameTracking(FBTrackingEvent.Marketing_Campaign, 1, payload);
        if(hasFBProperties) {
            track('mAdId', String(entrypointData.fb_instant_game_ad_id));
            track('mAdsetID', String(entrypointData.fb_instant_game_adset_id));
            track('mCampaignID', String(entrypointData.fb_instant_game_campaign_id));
            track('mAdName', payload.mAdName);
            track('attributionCampaignID', payload.mAdName);
            track('mAdType', payload.mAdType);
            track('mChannel', payload.mChannel);
            track('mOrigin', EntryOrigin.Marketing);
            FBPixel.track("marketing_campaign", {
                mAdId: entrypointData.fb_instant_game_ad_id,
                mAdsetID: entrypointData.fb_instant_game_adset_id,
                mCampaignID: entrypointData.fb_instant_game_campaign_id,
                mAdName: payload.mAdName,
                mAdType: payload.mAdType,
                mChannel: payload.mChannel,
            });
        }
        return {
            ...state,
            playerState: {
                ...state.playerState,
                attributionCampaignID: payload.mAdName
            }
        };
    }
}
